import { AxiosError } from "axios";

import i18n from "multitenancy/i18n";

import api, { getAcceptLanguage } from "services/api";
import { routers_constants } from "services/constants";
import { returnResponse } from "services/utils";

import { LogOperation } from "utils/entities/logOperation";
import { getFileName } from "utils/filename";

export interface IHearingFileBody {
  id: number;
  hearingId: number;
  type: number;
  hearingFileTypeId?: number | null;
  confidential?: boolean;
  attendantId?: number | null;
  title: string;
  filename: string;
  recordId: string;
  duration: number;
  size: number;
  fileOrder: number;
  hash: string;
  expectedDate: Date | null;
  startDate: Date | null;
  finishDate: Date | null;
  createIn: string;
  updateIn: Date | null;
  download: boolean;
  upload: boolean;
  watermark: number;
  platform: number;
  status: number;
  userId: number;
  device: string | null;
  ip: string | null;
  logPoliceUnitId: number;
  logUserId: number;
  parentId?: number | null;
}

export interface IHearingFileDownloads {
  filename: string;
  hearingFileId: number;
  size: number;
  type: number;
  url: string;
  confidential?: boolean;
  userId?: number;
  parentId?: number;
  name?: string;
  role?: string;
}

export interface IHearingFile {
  hearingId: number;
  type: number;
  title: string;
  filename: "inqueritodigitalssppc\\2023\\unidade_1\\foto\\Captura de Tela 2023-04-22 às 14.54.38.png";
  duration: number;
  size: number;
  fileOrder: number;
  hash: string; // uuid
  createIn: Date;
  updateIn: Date;
  upload: boolean;
}

interface IBaseAPIResponseGeneric<T> {
  status: number;
  data: {
    data: T;
    errors: [];
    message: string;
    ok: boolean;
  };
}

interface IBaseRestAPIResponse {
  data: any;
  status: number;
}

interface IUploadLargeFileStreamParams {
  file: File;
  hearingId: number;
  accessToken: string;
  hearingFileId?: number;
  mainVideo?: boolean;
  parentId?: number;
  onProgress?: (percentage: number) => void;
}

export const hearingFileService = {
  get: async (hearingFileId: number) => {
    try {
      const { data } = await api.get(`/v1/HearingFile/Get?id=${hearingFileId}`);

      return {
        status: true,
        data: data.data,
        message: data.message,
      };
    } catch {
      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },
  getAll: async (hearingId: string) => {
    try {
      const { data } = await api.get(
        `/v1/HearingFile/GetAllByHearingId?hearingId=${hearingId}`,
      );

      return {
        success: true,
        body: data.data,
      };
    } catch {
      return {
        success: false,
        body: {},
      };
    }
  },
  getAllDownloadFiles: async (hearingId: number) => {
    try {
      const { data } = await api.get(
        `/v1/HearingFile/GetAllDownloadsByHearingId?hearingId=${hearingId}`,
      );

      return {
        status: true,
        data: data.data,
        message: data.message,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },
  getAllDownloadsWithoutUrlByHearingId: async (hearingId: number) => {
    try {
      const uri = `/v1/HearingFile/GetAllDownloadsWithoutUrlByHearingId?hearingId=${hearingId}`;
      const { data, status } =
        await api.get<IBaseAPIResponseGeneric<Array<IHearingFileDownloads>>>(
          uri,
        );
      return { data, status };
    } catch (error) {
      const errorReturn = error as AxiosError;
      const { data, status } = errorReturn.response as IBaseRestAPIResponse;
      return { data, status };
    }
  },
  uploadFile: async (containerName: string, file: any) => {
    try {
      const { data } = await api.post(
        `/v1/hearingfile/uploadFile?container=${containerName}`,
        file,
        {
          onUploadProgress(progressEvent) {
            if (progressEvent && progressEvent.total) {
              Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          },
        },
      );

      return {
        success: true,
        body: data.data,
      };
    } catch {
      return {
        success: false,
        body: {},
      };
    }
  },
  add: async (payload: any, hearingId: any) => {
    try {
      const { data } = await api.post("/v1/HearingFile/Add", {
        hearingId,
        ...payload,
      });

      return {
        success: true,
        body: data.data,
      };
    } catch {
      return {
        success: false,
        body: {},
      };
    }
  },
  patch: async (field: string, id: number, value: number) => {
    try {
      const device = LogOperation.getDevice();
      const ip = LogOperation.getMachineIP();
      const logPoliceUnitId = LogOperation.getPoliceUnitId();
      const logUserId = LogOperation.getUserId();

      const body = [
        {
          op: "replace",
          value: value,
          path: field,
        },
        {
          op: "2",
          value: device,
          path: "device",
        },
        {
          op: "2",
          value: ip,
          path: "ip",
        },
        {
          op: "2",
          value: logPoliceUnitId,
          path: "logPoliceUnitId",
        },
        {
          op: "2",
          value: logUserId,
          path: "logUserId",
        },
      ];
      const { data } = await api.patch(`/v1/HearingFile/Update?id=${id}`, body);

      return {
        success: true,
        body: data,
      };
    } catch {
      return {
        success: false,
        body: {},
      };
    }
  },
  delete: async (id: number) => {
    try {
      const { data } = await api.delete(`/v1/HearingFile/Delete?id=${id}`);

      await LogOperation.postLogOperation("HearingFile", 3, 3);
      return {
        success: true,
        body: data.data,
      };
    } catch {
      return {
        success: true,
        body: {},
      };
    }
  },
  canBePrincipal: async (hearingId: number) => {
    try {
      const { data } = await api.get(
        `/v1/HearingFile/CanBePrincipal?hearingId=${hearingId}`,
      );
      await LogOperation.postLogOperation("HearingFile", 1, 1);
      return {
        success: data.ok,
        body: data.data,
      };
    } catch {
      return {
        success: false,
        body: {},
      };
    }
  },
  getUrlById: async (hearingFileId: number) => {
    try {
      const { data } = await api.get(
        `/v1/HearingFile/GetUrlByHearingFileId?hearingFileId=${hearingFileId}`,
      );

      return {
        success: data.ok,
        body: data.data,
      };
    } catch {
      return {
        success: false,
        body: {},
      };
    }
  },
  getTranscriptionByHearingFileId: async (hearingFileId: number) => {
    try {
      const { data } = await api.get(
        `/v1/HearingTranscription/GetTranscriptionEmbeded?hearingFileId=${hearingFileId}`,
      );

      if (data.ok) {
        return {
          success: true,
          body: data.data,
        };
      } else {
        return {
          success: false,
          message: "Nenhuma transcrição encontrada.",
        };
      }
    } catch {
      return {
        success: false,
        message: "Erro ao buscar a transcrição.",
      };
    }
  },

  downloadHearingFileById: async (hearingFileId: number) => {
    try {
      const response = await api.get(
        `/v1/HearingFile/DownloadHearingFileById?hearingFileId=${hearingFileId}`,
        {
          responseType: "blob", // Garante que a resposta seja tratada como um Blob
        },
      );

      // Verifique se o dado retornado é um Blob
      if (!(response.data instanceof Blob)) {
        throw new Error("A resposta não é um Blob válido.");
      }

      // Extração do nome do arquivo do cabeçalho
      const contentDisposition = response.headers["content-disposition"];
      const fileName = getFileName(contentDisposition);

      return {
        success: true,
        body: {
          blob: response.data,
          fileName: fileName,
        },
      };
    } catch {
      return {
        success: false,
        body: {},
      };
    }
  },

  sendLink: async (
    hearingFileId: number,
    emails: string[],
    attachmentIds?: number[],
  ) => {
    try {
      const { data } = await api.post("/v1/HearingFile/SendLink", {
        hearingFileId,
        emails,
        attachmentIds,
      });

      return {
        success: data.ok,
        message: data.message,
        data: data.data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "Erro ao enviar link",
        });

        return {
          severity,
          success: status,
          message: message ? message : "Erro ao enviar link",
          data,
        };
      }

      return {
        success: false,
        message: "Erro ao enviar link",
        data: null,
        severity: "error",
      };
    }
  },

  getHearingFileLinkByToken: async (token: string, code?: string) => {
    try {
      let url = `/v1/HearingFile/GetHearingFileLinkByToken?token=${token}`;
      if (code) {
        url += `&code=${code}`;
      }
      const { data } = await api.get(url);

      return {
        success: data.ok,
        message: data.message,
        data: data.data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const statusCode = error.response?.status;
        const responseData = error?.response?.data;
        const apiMessage: string = responseData?.message || "";

        if (statusCode === 423) {
          return {
            success: false,
            message: apiMessage,
            data: null,
            requiresMfa: true,
          };
        }

        if (statusCode === 429) {
          const errorsArray = responseData?.errors;
          const cooldownMessage =
            Array.isArray(errorsArray) && errorsArray.length > 0
              ? errorsArray.at(0)?.message
              : null;
          return {
            success: false,
            message: cooldownMessage || apiMessage,
            data: null,
            requiresMfa: true,
            isCooldown: true,
          };
        }

        const errorsArray = responseData?.errors;
        const errorMessage =
          (Array.isArray(errorsArray) && errorsArray.length > 0
            ? errorsArray.at(0)?.message
            : null) ||
          apiMessage ||
          i18n.t("recordingViewer.loadError");

        const { severity, status, message, data } = returnResponse({
          statusCode,
          data: responseData,
          message: errorMessage,
        });

        return {
          severity,
          success: status,
          message: message || errorMessage,
          data,
        };
      }

      return {
        success: false,
        message: "Erro ao buscar link",
        data: null,
        severity: "error",
      };
    }
  },

  getDownloadToken: async (hearingFileId: number, sharedToken?: string) => {
    try {
      let url = `/v1/HearingFile/GetDownloadToken?hearingFileId=${hearingFileId}`;
      if (sharedToken) url += `&sharedToken=${sharedToken}`;
      const { data } = await api.get(url);

      return {
        success: data.ok,
        message: data.message,
        data: data.data as {
          token: string;
          downloadUrl: string;
          fileName: string;
          expiresAt: string;
        },
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "Erro ao gerar token de download",
        });

        return {
          severity,
          success: status,
          message: message || "Erro ao gerar token de download",
          data,
        };
      }

      return {
        success: false,
        message: "Erro ao gerar token de download",
        data: null,
        severity: "error",
      };
    }
  },
};

export const hearingFileServiceV2 = {
  getAllDownloadsByHearingId: async (hearingId: number) => {
    try {
      const uri =
        routers_constants.hearingFile.getAllDownloadsByHearingId(hearingId);
      const { data, status } =
        await api.get<IBaseAPIResponseGeneric<Array<IHearingFileDownloads>>>(
          uri,
        );
      return { data, status };
    } catch (error) {
      const errorReturn = error as AxiosError;
      const { data, status } = errorReturn.response as IBaseRestAPIResponse;
      return { data, status };
    }
  },
  deleteByObject: async (payload: IHearingFileBody) => {
    try {
      const uri = routers_constants.hearingFile.deleteByObject;
      const { data, status } = await api.delete<
        IBaseAPIResponseGeneric<IHearingFileBody>
      >(uri, payload as any);
      return { data, status };
    } catch (error) {
      const errorReturn = error as AxiosError;
      const { data, status } = errorReturn.response as IBaseRestAPIResponse;
      return { data, status };
    }
  },
  deleteById: async (hearingFileId: number) => {
    try {
      const uri = routers_constants.hearingFile.deleteById(hearingFileId);
      const { data, status } =
        await api.delete<IBaseAPIResponseGeneric<boolean>>(uri);
      return { data, status };
    } catch (error) {
      const errorReturn = error as AxiosError;
      const { data, status } = errorReturn.response as IBaseRestAPIResponse;
      return { data, status };
    }
  },
  update: async (payload: IHearingFileBody) => {
    try {
      const uri = routers_constants.hearingFile.upload;
      const { data, status } = await api.put<IBaseAPIResponseGeneric<boolean>>(
        uri,
        payload,
      );
      return { data, status };
    } catch (error) {
      const errorReturn = error as AxiosError;
      const { data, status } = errorReturn.response as IBaseRestAPIResponse;
      return { data, status };
    }
  },
  patch: async (
    payload: { op: "replace"; value: string; path: string },
    id: number,
  ) => {
    try {
      const uri = routers_constants.hearingFile.patch(id);
      const { data, status } = await api.patch(uri, [payload]);
      return { data, status };
    } catch (error) {
      const errorReturn = error as AxiosError;
      const { data, status } = errorReturn.response as IBaseRestAPIResponse;
      return { data, status };
    }
  },
  add: async (payload: IHearingFileBody) => {
    try {
      const uri = routers_constants.hearingFile.add;
      const { data, status } = await api.post<IBaseAPIResponseGeneric<number>>(
        uri,
        payload,
      );
      return { data, status };
    } catch (error) {
      const errorReturn = error as AxiosError;
      const { data, status } = errorReturn.response as IBaseRestAPIResponse;
      return { data, status };
    }
  },
  uploadLargeFileStream: async ({
    file,
    hearingId,
    accessToken,
    hearingFileId,
    mainVideo,
    parentId,
    onProgress,
  }: IUploadLargeFileStreamParams) => {
    const uri = routers_constants.hearingFile.uploadLargeFileStream;
    const formData = new FormData();
    const requestUrl = `${api.defaults.baseURL || ""}${uri}`;

    formData.append("file", file);
    formData.append("hearingId", String(hearingId));

    if (typeof mainVideo === "boolean") {
      formData.append("mainVideo", String(mainVideo));
    }

    if (hearingFileId) {
      formData.append("hearingFileId", String(hearingFileId));
    }

    if (parentId) {
      formData.append("parentId", String(parentId));
    }

    return new Promise<{ data: any; status: number }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", requestUrl, true);
      xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
      xhr.setRequestHeader("Accept-Language", getAcceptLanguage());

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress((event.loaded / event.total) * 100);
        }
      };

      xhr.onerror = () => {
        reject(new Error("Falha de rede ao enviar requisicao."));
      };

      xhr.onload = () => {
        let data = null;

        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = { raw: xhr.responseText };
        }

        resolve({ data, status: xhr.status });
      };

      xhr.send(formData);
    });
  },
  getActiveRoom: async () => {
    try {
      const { data } = await api.get(`/v1/HearingFile/GetActiveRoom`);

      if (data.ok) {
        return {
          success: true,
          body: data.data,
        };
      } else {
        return {
          success: false,
          message: "Nenhuma sala encontrada.",
        };
      }
    } catch {
      return {
        success: false,
        message: "Erro ao buscar a salas em andamento.",
      };
    }
  },
};
